import React, { useCallback, useEffect, useRef, useState } from "react";
import { Table } from "reactstrap";
import PropTypes from "prop-types";
import { Tooltip as ReactTooltip } from "react-tooltip";
import TableStyle from "./../../../../components/table/table.module.scss";
import TableActionBtnGroup from "../../../../components/table/all-tables/tableactionbtngroup/TableActionBtnGroup";
import { downloadFileFromResponse, showToast } from "../../../../utils/utils";
import { showToastType } from "../../../../utils/constants/appConstants";
import { checkListTab, tooltips } from "../../../../locales/en";
import privateAxios from "../../../../utils/axiosPrivate";
import {
  getReportingCommandAnswerUrl,
  getReportingDeleteQuestionFileUrl,
  getReportingDownloadQuestionFileUrl,
  getReportingUploadQuestionFileUrl
} from "../../../../utils/constants/apiEndPoints";
import AutoResizeTextarea from "./AutoResizeTextarea";
import { CHECKLIST_TOOLTIP_CONFIG } from "../../../../utils/constants/tooltipsConfig";

const Questions = ({ sendData }) => {
  const [sendAnswerData, setSendAnswerData] = useState([]);
  const originalAnswersRef = useRef({});

  const triggerFileUpload = useCallback(
    (questionId) => async () => {
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true;
      input.accept = "application/pdf,image/*";

      input.onchange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        try {
          const formData = new FormData();
          files.forEach((file) => {
            formData.append("files", file);
          });
          formData.append("useIn", false);
          const response = await privateAxios.put(
            getReportingUploadQuestionFileUrl(questionId),
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );

          setSendAnswerData((prevData) =>
            prevData?.map((item) =>
              item.id === questionId
                ? { ...item, adminAttachedFiles: response?.data?.data }
                : item
            )
          );
          showToast(response?.data?.message, showToastType.success);
        } catch (error) {
          showToast(error?.response?.data?.message, showToastType.error);
        }
      };

      input.click();
    },
    [privateAxios]
  );

  const handleDeleteClick = useCallback(
    async (questionId, filePath) => {
      try {
        const response = await privateAxios.delete(
          getReportingDeleteQuestionFileUrl,
          {
            params: {
              questionId: questionId,
              filePath: filePath,
              useIn: false
            }
          }
        );
        setSendAnswerData((prevData) =>
          prevData?.map((item) =>
            item.id === questionId
              ? {
                  ...item,
                  adminAttachedFiles: item.adminAttachedFiles?.filter(
                    (file) => file !== filePath
                  )
                }
              : item
          )
        );
        showToast(response?.data?.message, showToastType.success);
      } catch (error) {
        showToast(error?.message, showToastType.error);
      }
    },
    [privateAxios]
  );

  const handleDownloadClick = useCallback(
    async (filePath) => {
      try {
        const response = await privateAxios.get(
          getReportingDownloadQuestionFileUrl,
          {
            params: { filePath: filePath },
            responseType: "blob"
          }
        );
        downloadFileFromResponse(response);
        showToast(
          checkListTab?.questions?.fileDownloadSuccess,
          showToastType.success
        );
      } catch {
        showToast(
          checkListTab?.questions?.fileDownloadFailed,
          showToastType.error
        );
      }
    },
    [privateAxios]
  );

  const handleAddAnswer = useCallback(
    (questionId, newAnswerText) => {
      const originalAnswer = originalAnswersRef.current[questionId] || "";
      if (newAnswerText === originalAnswer) {
        return;
      }

      privateAxios
        .put(getReportingCommandAnswerUrl, null, {
          params: { questionId, answerText: newAnswerText }
        })
        .then((response) => {
          showToast(response?.data?.message, showToastType.success);
          originalAnswersRef.current[questionId] = newAnswerText;
          setSendAnswerData((prevData) =>
            prevData?.map((item) =>
              item.id === questionId
                ? { ...item, answerText: newAnswerText }
                : item
            )
          );
        })
        .catch((error) => {
          showToast(error?.message, showToastType.error);
        });
    },
    [privateAxios]
  );

  const handleAnswerTextChange = useCallback((questionId, newText) => {
    setSendAnswerData((prevData) =>
      prevData.map((item) =>
        item.id === questionId ? { ...item, answerText: newText } : item
      )
    );
  }, []);

  useEffect(() => {
    if (Array.isArray(sendData)) {
      const sortedData = [...sendData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setSendAnswerData(sortedData);
      const map = {};
      sortedData.forEach((item) => {
        map[item.id] = item.answerText || "";
      });
      originalAnswersRef.current = map;
    } else {
      setSendAnswerData([]);
      originalAnswersRef.current = {};
    }
  }, [sendData]);

  Questions.propTypes = {
    sendData: PropTypes.any
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <Table
            className={`${TableStyle.commonCustomtable} checklist-table no-border mb-5`}
          >
            <thead>
              <tr>
                <td>{checkListTab?.questions.td1}</td>
              </tr>
            </thead>
            <tbody>
              {sendAnswerData?.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="d-flex align-items-start justify-content-between gap-2 w-100">
                      <div className="d-flex align-items-start gap-2">
                        <div className="mb-10">
                          <strong>{checkListTab?.questions.text1}</strong>
                          {item?.questionText}
                        </div>
                      </div>
                      <TableActionBtnGroup
                        upload={triggerFileUpload(item.id)}
                        tooltips={{
                          ...CHECKLIST_TOOLTIP_CONFIG,
                          upload: {
                            ...CHECKLIST_TOOLTIP_CONFIG.upload,
                            id: `tooltip-upload-${item.id}`,
                            place: "left"
                          }
                        }}
                      />
                    </div>

                    <div className="mt-4 mb-4">
                      <strong>{checkListTab?.questions.text2}</strong>
                      <AutoResizeTextarea
                        className="form-control mb-3 mt-1"
                        value={item?.answerText || ""}
                        onChange={(val) => handleAnswerTextChange(item.id, val)}
                        onBlur={(val) => handleAddAnswer(item.id, val)}
                        maxLines={5}
                      />
                    </div>
                    <div className="d-flex flex-column gap-1">
                      {item.attachedFiles?.map((filePath, index) => (
                        <div
                          key={"user-" + index}
                          className="d-flex align-items-center justify-content-between text-truncate"
                        >
                          <button
                            type="button"
                            className="btn btn-link p-0 text-primary text-decoration-underline"
                            onClick={() => handleDownloadClick(filePath)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleDownloadClick(filePath);
                              }
                            }}
                          >
                            {filePath
                              .replace(/^answer\/\d+\//, "")
                              .replace(/(.*)(_\d+_\d+)(\.\w+)$/, "$1$3")}
                          </button>
                        </div>
                      ))}
                      {item.adminAttachedFiles?.map((filePath, index) => (
                        <div
                          key={"admin-" + index}
                          className="d-flex align-items-center  justify-content-start text-truncate gap-2"
                        >
                          <button
                            type="button"
                            className="btn btn-link p-0 text-primary text-decoration-underline"
                            onClick={() => handleDownloadClick(filePath)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleDownloadClick(filePath);
                              }
                            }}
                          >
                            {filePath
                              .replace(/^answer\/\d+\//, "")
                              .replace(/(.*)(_\d+_\d+)(\.\w+)$/, "$1$3")}
                          </button>
                          <TableActionBtnGroup
                            onDelete={() =>
                              handleDeleteClick(item.id, filePath)
                            }
                            tooltips={{
                              ...CHECKLIST_TOOLTIP_CONFIG,
                              onDelete: {
                                ...CHECKLIST_TOOLTIP_CONFIG.onDelete,
                                id: `tooltip-delete-${item.id}-${index}`
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
      <ReactTooltip
        className="tooltipcustom"
        id="tooltip-upload"
        content={tooltips.uploadFile}
        place="top"
      />
    </>
  );
};

export default Questions;
